import React, {useEffect, useState} from "react";
import Row from "react-bootstrap/Row";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faArrowRight, faDownload, faRetweet} from "@fortawesome/free-solid-svg-icons";
import Container from "react-bootstrap/Container";
import {Spinner} from "react-bootstrap";
import axios from "axios";
import {axiosErrorHandler} from "./common/util/AxiosUtil";
import Alert from "react-bootstrap/Alert";
import VehicleHiresList from "./vehicleHire/VehicleHiresList";
import FilterForm, {Filter} from "./FilterForm";
import FilterParamType from "./common/enum/FilterParamType";
import {extractFirstHeaderLink} from "./util/LinkHeaderUtil";
import VehicleHiresTable from "./vehicleHire/VehicleHiresTable";
import {API_ORIGIN} from "../services/API";
import {useAppSelector} from "../store";
import {selectedCustomerSelector} from "../store/slice/auth";

const VehicleHiresPage = () => {

    const [ errorMessage, setErrorMessage ] = useState<string | null>(null);
    const [ stateLoading, setStateLoading ] = useState<boolean>(false);
    const [ statePreviousLinks, setStatePreviousLinks ] = useState<string[]>([]);
    const [ stateCurrentLink, setStateCurrentLink ] = useState<string | null>(null);
    const [ stateNextLink, setStateNextLink ] = useState<string | null>(null);
    const [ stateVehicleHires, setStateVehicleHires ] = useState<CustomerAPIVehicleHire[]>([]);
    const [ stateExpandedIds, setStateExpandedIds ] = useState<Set<number>>(new Set());
    const [ stateFilters, setStateFilters ] = useState<Filter[]>([]);

    const topOfList = React.useRef<HTMLDivElement>(null);

    const selectedCustomer = useAppSelector(selectedCustomerSelector);

    const resetPagination = () => {
        setStatePreviousLinks([]);
        setStateCurrentLink(buildUrl(false));
        setStateNextLink(null);
    };

    const onFiltersChanged = (filters: Filter[]) => {
        if (filters !== stateFilters) {
            setStateFilters(filters);
            resetPagination();
        }
    };

    const scrollToTopOfList = () => {
        topOfList.current?.scrollIntoView({behavior: 'smooth'});
    }


    const gotoNext = () => {
        setStatePreviousLinks(prevState => {
            return [...prevState, stateCurrentLink!];
        });
        setStateCurrentLink(stateNextLink);
        setStateNextLink(null);
    };

    const gotoPrevious = () => {
        const previous = statePreviousLinks.pop();
        setStatePreviousLinks([...statePreviousLinks]) // we have popped the last one
        setStateCurrentLink(previous ?? buildUrl(false));
        setStateNextLink(null)
    };

    const buildUrl = (isCSV: boolean) => {

        let path = `${API_ORIGIN}/customer/v1/vehicle-hires`;
        const searchParams = new URLSearchParams();

        if (isCSV) {
            if (selectedCustomer && selectedCustomer.id) {
                searchParams.set("customerId", String(selectedCustomer.id));
            }
            path += '/csv';
        }

        searchParams.set("limit", "25");
        stateFilters.forEach(filter => {
            searchParams.set(filter.type.queryParam, filter.type.formatForQueryParam(filter.value));
        });
        const queryParams = searchParams.toString();
        if (queryParams) {
            return path + `?${queryParams}`
        }
        return path;
    };

    const loadVehicleHires = (scroll = true) => {
        const url = stateCurrentLink;
        if (!url) {
            return
        }

        setStateLoading(true);

        axios.get<CustomerAPIVehicleHire[]>(url)
            .then((response) => {
                const nextLink = extractFirstHeaderLink(response, 'next');
                setStateVehicleHires(response.data);
                setStateNextLink((nextLink) ? nextLink.uri : null);
                if (scroll) {
                    scrollToTopOfList();
                }
            })
            .catch((error) => {
                axiosErrorHandler(error, errorMessage => setErrorMessage(errorMessage));
            })
            .finally(() => setStateLoading(false));
    };

    const handleToggleExpand = (id: number) => {
        setStateExpandedIds(prevState => {
            const copy = new Set(prevState);
            if (copy.has(id)) {
                copy.delete(id)
            } else {
                copy.add(id)
            }
            return copy
        })
    }

    useEffect(() => {
        if (!stateCurrentLink) {
            setStateCurrentLink(buildUrl(false));
        }
    }, [stateCurrentLink]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        resetPagination();
        loadVehicleHires(false);
    }, [selectedCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (stateCurrentLink) {
            loadVehicleHires(false)
        }
    }, [stateCurrentLink]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setStateCurrentLink(buildUrl(false))
    }, [stateFilters]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Container>
            <Row className="justify-content-end align-items-center">
                <div className="px-1 col-6 col-sm-8">
                    {/* <h3 className="p-1 p-sm-3">Hires</h3> */}
                </div>
                <div className="px-1 col-6 col-sm-4">
                    <a className="btn btn-outline-dark float-end" href={`${buildUrl(true)}`} target="_blank" rel="noopener noreferrer">
                        CSV <FontAwesomeIcon icon={faDownload}/>
                    </a>
                    <button className="btn btn-outline-dark float-end me-2" onClick={() => loadVehicleHires(false)}>
                        <FontAwesomeIcon icon={faRetweet}/>
                    </button>
                </div>
            </Row>

            <FilterForm
                onFiltersChanged={onFiltersChanged}
                filters={[
                    FilterParamType.HIRED_FOR_DATE,
                    FilterParamType.HIRE_REF_CONTAINS
                ]}
                selectedFilter={
                    FilterParamType.HIRED_FOR_DATE
                }
                initialFilters={[]}
            />

            {errorMessage &&
                <Alert variant="danger" onClose={() => setErrorMessage(null)} dismissible>
                    <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
                    <p>{errorMessage}</p>
                </Alert>
            }

            <div ref={topOfList} />

            {stateLoading
                ? <div className="text-center"><Spinner animation={"border"} /></div>
                : <>
                    <div className="d-none d-xl-block">
                        <VehicleHiresTable
                            vehicleHires={stateVehicleHires}
                            expandedIds={stateExpandedIds}
                            onToggleExpand={handleToggleExpand}
                        />
                    </div>
                    <div className="d-xl-none d-lg-block">
                        <VehicleHiresList
                            vehicleHires={stateVehicleHires}
                            expandedIds={stateExpandedIds}
                            onToggleExpand={handleToggleExpand}
                        />
                    </div>
                  </>
            }

            <Row>
                <div className="col-12 pt-3">
                    <button className="btn btn-outline-dark float-start" disabled={statePreviousLinks.length < 1} onClick={gotoPrevious}>
                        <FontAwesomeIcon icon={faArrowLeft}/> Previous
                    </button>
                    <button className="btn btn-outline-dark float-end" disabled={!stateNextLink} onClick={gotoNext}>
                        Next <FontAwesomeIcon icon={faArrowRight}/>
                    </button>
                </div>
            </Row>

        </Container>
    )

}

export default VehicleHiresPage;
